/**
 * «Открыть бюджет сезона» + честные состояния «работать с бюджетом пока
 * нельзя» — общее для хаба, бюджетных справочников, плановых цен и техкарт
 * (AK-648).
 *
 * UAT founder 18.08 (staging v1.1693): «бюджет сезона нельзя открыть из UI».
 * Разбор корня:
 *
 *   1. Список сезонов ВИРТУАЛЬНЫЙ (ADR-060 §S6): год порождает правило
 *      календаря, строка `seasons` появляется с первой записью в сезоне. У
 *      планового сезона записей нет → карточка приходит с `season_id: null`.
 *   2. Фронт объявлял `season_id` обязательным и такого состояния не знал:
 *      ветка «сезона нет» не срабатывала (карточка-то есть), а `seasonId` был
 *      null. Экраны бюджета рисовали ПУСТОТУ под шапкой, «Наполнить
 *      справочники» молча не открывала мастер.
 *   3. Текст «откройте бюджет на странице Планирование сезона» отправлял по
 *      кругу — такого действия в хабе не было вовсе.
 *
 * Действие адресуется ГОДОМ (`POST /planning/seasons/{year}/open-budget`):
 * ручка сама материализует сезон и открывает план хозяйствам. Кнопка по
 * `season_id` была бы невозможна ровно там, где она нужна.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { FolderOpen, Settings2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Stack } from '@/components/ui/stack';
import { describeApiError } from '@/lib/apiError';
import { useCan } from '@/lib/auth/usePermissions';
import { openSeasonBudget } from '@/lib/planning/api';
import { toast } from '@/lib/toast';

/** Право «вести бюджет сезона»: открыть / утвердить / откатить (`season_plan:U`). */
export function useCanOpenSeasonBudget(): boolean {
  return useCan('season_plan', 'u');
}

/**
 * «Открыть бюджет сезона {год}»: сезон заводится строкой, хозяйства входят в
 * цикл планирования. Идемпотентно — повторное нажатие безопасно.
 *
 * Без права ничего не рисуем: кнопка, которая гарантированно даст 403, — это
 * «толкай и жди», а не интерфейс.
 */
export function OpenSeasonBudgetButton({
  year,
  companyId,
  size,
  variant,
  onOpened,
}: {
  year: number;
  companyId?: string | null;
  size?: 'sm' | 'default';
  variant?: 'default' | 'outline';
  /**
   * Чем закончить успех. По умолчанию — переход в бюджетные справочники
   * (сезон открыт → первое, что делают, это наполняют справочники). Экран,
   * который сам себя оживит инвалидацией, передаёт свой обработчик и
   * остаётся на месте.
   */
  onOpened?: () => void;
}) {
  const canOpen = useCanOpenSeasonBudget();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const open = useMutation({
    mutationFn: () => openSeasonBudget(year, { companyId }),
    onSuccess: () => {
      // Карточки сезонов несут `season_id` и статус бюджета — до перечитки
      // экран продолжал бы считать сезон незаведённым.
      void queryClient.invalidateQueries({ queryKey: ['planning', 'season-cards'] });
      void queryClient.invalidateQueries({ queryKey: ['planning', 'season-plan'] });
      toast.success(`Бюджет сезона ${year} открыт`);
      if (onOpened) {
        onOpened();
        return;
      }
      void navigate({
        to: '/planning/seasons/$year/budget-catalogs',
        params: { year: String(year) },
      });
    },
    // Молчащая кнопка — та же болезнь, что чиним: отказ показываем текстом
    // бэка, а не в консоли.
    onError: (err) => toast.error(describeApiError(err)),
  });

  if (!canOpen) return null;

  return (
    <Button size={size} variant={variant} disabled={open.isPending} onClick={() => open.mutate()}>
      <FolderOpen className="mr-2 h-4 w-4" />
      {open.isPending ? 'Открываем…' : `Открыть бюджет сезона ${year}`}
    </Button>
  );
}

/**
 * Сезон ещё не открыт (`season_id === null`): вести его бюджет нельзя ничем —
 * справочники, цены и техкарты адресуются сезоном. Даём ровно то действие,
 * которого не хватало, а без права — честно говорим, к кому идти.
 */
export function SeasonNotStartedCard({
  year,
  companyId,
  onOpened,
}: {
  year: number;
  companyId?: string | null;
  onOpened?: () => void;
}) {
  const canOpen = useCanOpenSeasonBudget();
  return (
    <Card>
      <CardContent className="p-4">
        <Stack gap={3}>
          <p className="text-sm text-foreground">
            Бюджет сезона {year} ещё не открыт — в этом сезоне пока не делали ни одной
            записи.
          </p>
          <p className="text-sm text-muted-foreground">
            {canOpen
              ? 'Откройте бюджет — сезон заведётся, хозяйства войдут в планирование, и справочники, цены и техкарты этого года станут доступны.'
              : 'Открыть бюджет сезона может тот, кто ведёт планирование, — попросите администратора компании.'}
          </p>
          <div className="flex flex-wrap gap-2">
            <OpenSeasonBudgetButton year={year} companyId={companyId} onOpened={onOpened} />
          </div>
        </Stack>
      </CardContent>
    </Card>
  );
}

/**
 * Года нет в списке вовсе (ни в окне видимости, ни среди годов с работой).
 * Отличается от `SeasonNotStartedCard` причиной и лекарством: тут не открывать
 * бюджет, а править окно сезонов — бэк такой год и не откроет
 * (`season_year_out_of_window`).
 */
export function SeasonYearOutOfWindowCard({ year }: { year: number }) {
  return (
    <Card>
      <CardContent className="p-4">
        <Stack gap={3}>
          <p className="text-sm text-foreground">
            Сезона {year} нет в списке сезонов компании.
          </p>
          <p className="text-sm text-muted-foreground">
            Скорее всего год за окном видимости: в настройках компании задано, сколько
            прошлых и будущих сезонов показывать. Расширьте окно — год появится в
            разделе «Планирование сезона».
          </p>
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline">
              <Link to="/company/$tab" params={{ tab: 'season' }}>
                <Settings2 className="mr-2 h-4 w-4" />
                Настройки компании · Сезон
              </Link>
            </Button>
            <Button asChild variant="ghost">
              <Link to="/planning">Все сезоны</Link>
            </Button>
          </div>
        </Stack>
      </CardContent>
    </Card>
  );
}
